import { getSupabase } from "./supabase";
import { notifyAuthSessionExpired } from "./authSessionExpired";

type FetcherError = Error & {
  status?: number;
  headers?: Headers;
  expected?: boolean;
  url?: string;
};

function createFetcherError(
  message: string,
  details: {
    status?: number;
    headers?: Headers;
    expected?: boolean;
    url?: string;
  } = {},
): FetcherError {
  const error = new Error(message) as FetcherError;
  if (details.status !== undefined) error.status = details.status;
  if (details.headers) error.headers = details.headers;
  if (details.expected !== undefined) error.expected = details.expected;
  if (details.url) error.url = details.url;
  return error;
}

type ParsedResponsePayload = {
  rawText: string;
  json: any | null;
  isJsonLike: boolean;
};

async function parseResponsePayload(response: Response): Promise<ParsedResponsePayload> {
  const rawText = await response.text();
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const trimmed = rawText.trim();
  const isJsonLike = contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[");

  if (!trimmed) {
    return { rawText, json: null, isJsonLike };
  }

  if (!isJsonLike) {
    return { rawText, json: null, isJsonLike };
  }

  try {
    return { rawText, json: JSON.parse(rawText), isJsonLike };
  } catch {
    return { rawText, json: null, isJsonLike: true };
  }
}

async function getAccessToken(refresh = false): Promise<string | null> {
  const supabase = getSupabase();
  const result = refresh ? await supabase.auth.refreshSession() : await supabase.auth.getSession();
  return result.data.session?.access_token || null;
}

async function fetchWithToken(
  url: string,
  token: string,
): Promise<{ response: Response; payload: ParsedResponsePayload }> {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return {
    response,
    payload: await parseResponsePayload(response),
  };
}

// SWR fetcher with Supabase auth
export async function authFetcher<T>(url: string): Promise<T> {
  let accessToken = await getAccessToken(false);

  if (!accessToken) {
    accessToken = await getAccessToken(true);
  }

  if (!accessToken) {
    notifyAuthSessionExpired({ source: "api" });
    throw createFetcherError("Not authenticated", { status: 401, expected: true, url });
  }

  let { response, payload } = await fetchWithToken(url, accessToken);

  if (response.status === 401) {
    const refreshedToken = await getAccessToken(true);
    if (refreshedToken) {
      ({ response, payload } = await fetchWithToken(url, refreshedToken));
    }
  }

  if (!response.ok) {
    const errorData = payload.json || {};

    // Handle "no connection" errors gracefully for Meta-related endpoints
    // These aren't errors - just empty state when Meta isn't connected
    // Support both string errors and structured { code, message } errors
    const rawError = errorData?.error;
    const errorMsg = (typeof rawError === "string" ? rawError : rawError?.message || "")?.toLowerCase() || "";
    const noConnectionError =
      errorMsg.includes("no facebook") ||
      errorMsg.includes("no meta") ||
      errorMsg.includes("connection not found") ||
      errorMsg.includes("no instagram");
    const isMetaRelatedEndpoint =
      url.includes("/api/ads/") ||
      url.includes("/api/attribution/") ||
      url.includes("/api/funnel/") ||
      url.includes("/api/facebook/");

    if (response.status === 400 && noConnectionError && isMetaRelatedEndpoint) {
      // Return empty response shape based on endpoint
      return {
        accounts: [],
        insights: [],
        totalSpend: 0,
        currency: "USD",
        posts: [],
        no_connection: true,
      } as T;
    }

    if (response.status === 401) {
      notifyAuthSessionExpired({ source: "api" });
      throw createFetcherError("Not authenticated", {
        status: 401,
        headers: response.headers,
        expected: true,
        url,
      });
    }

    // Extract error message from string or structured error
    const fallbackText = payload.rawText.trim();
    const errorMessage =
      (typeof rawError === "string" ? rawError : rawError?.message) ||
      (fallbackText && !fallbackText.startsWith("<") ? fallbackText.slice(0, 200) : "") ||
      `HTTP ${response.status}`;
    throw createFetcherError(errorMessage, { status: response.status, headers: response.headers, url });
  }

  if (response.status === 204 || !payload.rawText.trim()) {
    return undefined as T;
  }

  if (payload.json !== null) {
    return payload.json as T;
  }

  const trimmed = payload.rawText.trim();
  if (trimmed.startsWith("<")) {
    throw createFetcherError("Expected JSON response but received HTML", {
      status: response.status,
      headers: response.headers,
      url,
    });
  }

  if (payload.isJsonLike) {
    throw createFetcherError("Invalid JSON response payload", {
      status: response.status,
      headers: response.headers,
      url,
    });
  }

  throw createFetcherError("Expected JSON response", {
    status: response.status,
    headers: response.headers,
    url,
  });
}
